/* eslint-disable no-unused-vars */
import React, { useState } from "react";
import {
  Search,
  ChevronDown,
  Mail,
  MessageSquare,
  Phone,
  Book,
  ArrowRight,
  FileText,
} from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { ARTICLES } from "./Article";
import { faqs } from "../../../utils/constants";

const Help = () => {
  const navigate = useNavigate();
  const [activeFaq, setActiveFaq] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredArticles = ARTICLES.filter(
    (a) =>
      a.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.category.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 max-w-5xl mx-auto pb-16 bg-background dark:bg-dark-background min-h-screen p-4">
      {/* Hero Section */}
      <div className="text-center py-16 px-4 bg-gradient-to-b from-primary/5 to-transparent dark:from-primary/10 rounded-[3rem] mb-12">
        <h1 className="text-4xl md:text-5xl font-extrabold text-text dark:text-dark-text mb-6">
          How can we{" "}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-primary/80">
            help you?
          </span>
        </h1>
        <p className="text-lg text-gray-500 dark:text-dark-muted mb-10 max-w-2xl mx-auto">
          Search our knowledge base or get in touch with our support team for
          assistance.
        </p>

        <div className="max-w-2xl mx-auto relative group z-10">
          <div className="absolute -inset-1 bg-gradient-to-r from-primary to-primary/60 rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-200"></div>
          <div className="relative flex items-center bg-card dark:bg-dark-surface rounded-2xl shadow-xl">
            <Search
              className="ml-6 text-gray-400 dark:text-dark-muted"
              size={24}
            />
            <input
              type="text"
              placeholder="Search for answers, topics, or keywords..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-5 rounded-2xl bg-transparent border-none outline-none text-lg text-text dark:text-dark-text placeholder-gray-400 dark:placeholder-dark-muted"
            />
          </div>

          {/* Search Results Dropdown */}
          {searchTerm && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-card dark:bg-dark-surface rounded-2xl shadow-2xl border border-gray-100 dark:border-dark-border overflow-hidden max-h-80 overflow-y-auto custom-scrollbar animate-in fade-in zoom-in-95 duration-200">
              {filteredArticles.length > 0 ? (
                filteredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/help/article/${article.id}`}
                    className="block px-6 py-4 hover:bg-accent dark:hover:bg-gray-800/50 border-b border-gray-100 dark:border-dark-border/50 last:border-none text-left"
                  >
                    <p className="font-bold text-text dark:text-dark-text text-sm mb-1">
                      {article.title}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-dark-muted">
                      <span className="bg-accent dark:bg-gray-800 px-2 py-0.5 rounded text-gray-600 dark:text-dark-muted">
                        {article.category}
                      </span>
                      <span>{article.date}</span>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="p-6 text-gray-500 dark:text-dark-muted text-sm">
                  No articles found matching &quot;{searchTerm}&quot;
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Popular Articles Grid */}
      <div className="mb-16">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-text dark:text-dark-text">
            Popular Articles
          </h2>
          <button
            onClick={() => navigate("/404")}
            className="text-primary font-bold text-sm hover:underline"
          >
            View All
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {ARTICLES.slice(0, 6).map((article) => (
            <Link
              key={article.id}
              to={`/help/article/${article.id}`}
              className="flex items-start gap-4 p-5 rounded-2xl bg-card dark:bg-dark-surface border border-gray-100 dark:border-dark-border hover:shadow-lg hover:border-primary/30 dark:hover:border-primary/30 transition-all group"
            >
              <div className="p-3 bg-primary/10 dark:bg-primary/20 text-primary rounded-xl group-hover:scale-110 transition-transform">
                <FileText size={20} />
              </div>
              <div>
                <h3 className="font-bold text-text dark:text-dark-text mb-1 group-hover:text-primary transition-colors">
                  {article.title}
                </h3>
                <p className="text-sm text-gray-500 dark:text-dark-muted line-clamp-2">
                  {article.content}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Support Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
        {[
          {
            icon: MessageSquare,
            title: "Live Chat",
            desc: "Chat with our support team in real-time.",
            color: "blue",
            action: "Start Chat",
          },
          {
            icon: Mail,
            title: "Email Support",
            desc: "Get detailed answers within 24 hours.",
            color: "primary",
            action: "Send Email",
          },
          {
            icon: Phone,
            title: "Phone Call",
            desc: "Speak directly to an agent immediately.",
            color: "purple",
            action: "Call Us",
          },
        ].map((card, i) => (
          <div
            key={i}
            className="bg-card dark:bg-dark-surface p-8 rounded-[2rem] border border-gray-100 dark:border-dark-border text-center hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group cursor-pointer"
          >
            <div
              className={`w-16 h-16 mx-auto mb-6 rounded-2xl flex items-center justify-center transition-colors ${
                card.color === "primary"
                  ? "bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary"
                  : card.color === "blue"
                    ? "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400"
                    : "bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400"
              } group-hover:scale-110 duration-300`}
            >
              <card.icon size={32} />
            </div>
            <h3 className="text-xl font-bold text-text dark:text-dark-text mb-3">
              {card.title}
            </h3>
            <p className="text-gray-500 dark:text-dark-muted mb-6">
              {card.desc}
            </p>
            <div
              className={`inline-flex items-center font-bold ${
                card.color === "primary"
                  ? "text-primary dark:text-primary"
                  : card.color === "blue"
                    ? "text-blue-600 dark:text-blue-400"
                    : "text-purple-600 dark:text-purple-400"
              } group-hover:underline`}
            >
              {card.action} <ArrowRight size={16} className="ml-2" />
            </div>
          </div>
        ))}
      </div>

      {/* FAQ & Documentation Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* FAQs */}
        <div className="lg:col-span-8">
          <h2 className="text-2xl font-bold text-text dark:text-dark-text mb-8">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {faqs.map((item, i) => (
              <div
                key={i}
                className={`bg-card dark:bg-dark-surface rounded-2xl border transition-all duration-300 overflow-hidden ${
                  activeFaq === i
                    ? "border-primary/30 shadow-lg dark:shadow-none"
                    : "border-gray-100 dark:border-dark-border hover:border-primary/30"
                }`}
              >
                <button
                  onClick={() => setActiveFaq(activeFaq === i ? null : i)}
                  className="w-full flex justify-between items-center p-6 text-left"
                >
                  <span
                    className={`font-bold text-lg ${
                      activeFaq === i
                        ? "text-primary dark:text-primary"
                        : "text-text dark:text-dark-text"
                    }`}
                  >
                    {item.q}
                  </span>
                  <ChevronDown
                    size={20}
                    className={`text-gray-400 dark:text-dark-muted transition-transform duration-300 ${
                      activeFaq === i ? "rotate-180 text-primary" : ""
                    }`}
                  />
                </button>
                <div
                  className={`px-6 text-gray-500 dark:text-dark-muted leading-relaxed transition-all duration-300 ease-in-out ${
                    activeFaq === i
                      ? "max-h-40 pb-6 opacity-100"
                      : "max-h-0 opacity-0"
                  }`}
                >
                  {item.a}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Links / Docs */}
        <div className="lg:col-span-4 space-y-6">
          <h2 className="text-2xl font-bold text-text dark:text-dark-text mb-8">
            Documentation
          </h2>

          <div className="bg-gradient-to-br from-secondary to-gray-800 dark:from-gray-800 dark:to-gray-900 rounded-[2rem] p-8 text-white relative overflow-hidden">
            <div className="relative z-10">
              <Book size={32} className="mb-4 text-primary" />
              <h3 className="text-xl font-bold mb-2">User Guide</h3>
              <p className="text-gray-300 dark:text-gray-300 mb-6 text-sm">
                Download our comprehensive PDF guide for new caregivers.
              </p>
              <button className="w-full py-3 bg-primary hover:bg-primary/90 rounded-xl font-bold text-sm transition-colors">
                Download PDF
              </button>
            </div>
            <div className="absolute top-0 right-0 w-32 h-32 bg-primary/20 rounded-full blur-3xl -mr-10 -mt-10"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Help;
